import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RoleRight } from '@dep/common/interfaces';
import {
  type SupportFormPublic,
  supportFormPublic,
  type SupportForm,
} from '@dep/common/support-api/types/support-form.type';
import { hasFormAccess } from '@dep/common/utils/support-form/has-form-access.util';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom, Subscription } from 'rxjs';

import { environment } from '../../../environments/environment';

import { UserTokenService } from '@dep/frontend/users/services/user-token.service';
import { UserService } from '@dep/frontend/users/services/user.service';

@Injectable({
  providedIn: 'root',
})
export class SupportFormsService {
  private forms?: SupportFormPublic[];

  constructor(
    private logger: NGXLogger,
    private http: HttpClient,
    private userService: UserService,
    private userTokenService: UserTokenService,
  ) {
    this.clearFormsOnLogout();
  }

  public async getAllForms(): Promise<SupportFormPublic[]> {
    if (this.forms) {
      return this.forms;
    };

    try {
      const response = await lastValueFrom(this.http.get<SupportForm[]>(
        environment.config.supportv2.url + '/forms',
        {
          headers: await this.userTokenService.getAuthorizationHeaders(),
        },
      ));
      this.logger.debug('Support forms', response);

      const parsedForms = supportFormPublic.array().parse(response);
      this.forms = parsedForms;

      return parsedForms;
    } catch (err) {
      this.logger.error('Could not fetch forms', err);
      throw err;
    }
  }

  /**
   * Retrieves support forms filtered based on user permissions and access rights.
   *
   * Filtering is done in the frontend because the user needs to have access to all forms anyway, e. g. if they view a support ticket of a form to which they do
   * not have ticket create rights.
   *
   * - Support agents (users with SUPPORTTICKET_UPDATE right) receive all available forms
   * - Regular users receive only forms they have access to based on their roles and country
   */
  public async getFilteredForms(): Promise<SupportFormPublic[]> {
    const forms = await this.getAllForms();

    const user = await this.userService.getCurrentUser();
    if (!user) {
      this.logger.error('Cannot filter support forms: User authentication required but user data is unavailable');
      throw new Error('Cannot filter support forms: User authentication required but user data is unavailable');
    }

    const isSupportAgent = user.roles.some((userRole) => userRole.rights.includes(RoleRight.SUPPORTTICKET_UPDATE));
    if (isSupportAgent) {
      return forms;
    }

    return forms.filter((form) => hasFormAccess(form.visibility, user.roles, user.country));
  }

  /**
   * Subscription handler to clear the current forms on logout.
   *
   * @returns Subscription - Subscription the handler registrated.
   */
  private clearFormsOnLogout(): Subscription {
    return this.userService.sessionObservable$.subscribe({
      next: (user) => {
        if (!user) {
          this.forms = undefined;
        }
      },
    });
  }
}
